import Image from 'next/image';
import cn from 'classnames';

import { useSliderDispatch, useSliderState } from '@/components/Slider/context';
import { NavigationButton, NavigationPage } from './pagination';
import SocialMediaLinks from '../social-media-links';

export type Author = {
  name: string;
  title: string;
  bio: string;
  twitterHandle: string;
  website: string;
  linkedinUrl: string;
  gitHub: string;
  photo: {
    width: number;
    height: number;
    url: string;
    alt: string;
  };
};

export default function AuthorsSlider() {
  const { activeIndex, items } = useSliderState<Author>();
  const { nextItem, previousItem, goToItem } = useSliderDispatch();

  const containerWidth = `${items.length * 100}%`;

  const translateValue = () => {
    const width = 100 / items.length;

    return `-${activeIndex * width}%`;
  };

  return (
    <>
      <div
        className={cn(
          `lg:-mx-3 relative flex items-center justify-between mb-12 lg:mb-6`,
        )}
      >
        <div className="w-full grid grid-cols-1 lg:grid-cols-8 lg:gap-6">
          <div className="w-full h-20 lg:h-48 col-span-2 mb-4 lg:mb-0">
            <ul className="relative w-full h-full">
              {items.map((item, index) => {
                const isActive = activeIndex === index;

                return (
                  <li
                    className={cn(
                      `absolute bottom-0 duration-300 lg:h-full left-0 right-0 top-0 transition-opacity lg:w-full lg:flex justify-center items-center flex-col w-20 h-20`,
                      {
                        'opacity-0 z-0': !isActive,
                        'opacity-100 z-10': isActive,
                      },
                    )}
                    key={item.name}
                  >
                    {item?.photo?.url && (
                      <Image
                        src={item.photo.url}
                        title={item.name}
                        width={180}
                        height={180}
                        objectFit="cover"
                        className="rounded-full"
                        alt={item.name}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="flex flex-col overflow-hidden w-full rounded-lg lg:col-span-6">
            <ul
              className="duration-300 flex flex-1 transition-transform"
              style={{
                width: containerWidth,
                transform: `translateX(${translateValue()})`,
              }}
            >
              {items.map((author, index) => (
                <li
                  className="lg:px-5 flex justify-center flex-col w-full"
                  key={index}
                >
                  <div className="flex justify-between items-center w-full mb-3">
                    <h3 className="font-bold leading-none text-xl md:text-3xl">
                      {author.name}
                    </h3>
                    <SocialMediaLinks
                      className="hidden lg:flex"
                      gitHub={author.gitHub}
                      linkedinUrl={author.linkedinUrl}
                      twitterHandle={author.twitterHandle}
                      websiteUrl={author.website}
                    />
                  </div>
                  <p className="text-sm text-zodiac font-bold">
                    {author.title}
                  </p>
                  <p className="text-sm leading-normal text-light-haiti mt-4">
                    {author.bio}
                  </p>
                  <SocialMediaLinks
                    className="lg:hidden mt-4"
                    gitHub={author.gitHub}
                    linkedinUrl={author.linkedinUrl}
                    twitterHandle={author.twitterHandle}
                    websiteUrl={author.website}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {items.length > 1 && (
        <ul className="flex justify-center items-center space-x-2">
          <NavigationButton direction="prev" onClick={previousItem} />
          {items.map((_, index) => {
            const pageNumber = index + 1;
            const isCurrentPage = activeIndex === index;

            return (
              <NavigationPage
                isCurrentPage={isCurrentPage}
                key={index}
                onClick={() => goToItem(index)}
              >
                {pageNumber}
              </NavigationPage>
            );
          })}
          <NavigationButton direction="next" onClick={nextItem} />
        </ul>
      )}
    </>
  );
}
